import styled from 'styled-components'
import { Text } from '@pancakeswap/uikit'
import { useTranslation } from '@pancakeswap/localization'
import { motion } from 'framer-motion'
import useTheme from 'hooks/useTheme'
import { palette } from '../colors'

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  padding: 56px 0;

  ${({ theme }) => theme.mediaQueries.md} {
    padding: 80px 0;
    gap: 48px;
  }
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  max-width: 760px;
  text-align: center;
`

const Eyebrow = styled(Text)`
  font-family: 'Geist Mono', monospace;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 2px;
`

const Title = styled(Text)`
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -1px;
  line-height: 1.1;

  ${({ theme }) => theme.mediaQueries.md} {
    font-size: 48px;
    letter-spacing: -1.5px;
  }
`

const Grid = styled.div`
  display: grid;
  width: 100%;
  max-width: 1280px;
  grid-template-columns: 1fr;
  gap: 16px;

  ${({ theme }) => theme.mediaQueries.md} {
    grid-template-columns: repeat(3, 1fr);
  }
`

const DappCard = styled(motion.div)<{ $bg: string; $border: string }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 28px;
  border-radius: 20px;
  background: ${({ $bg }) => $bg};
  border: 1px solid ${({ $border }) => $border};
  box-shadow: 0 4px 24px rgba(10, 26, 51, 0.08);
  overflow: hidden;
`

const Icon = styled.div<{ $color: string }>`
  font-size: 32px;
  line-height: 1;
  color: ${({ $color }) => $color};
`

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const Tag = styled.div<{ $card: string; $border: string; $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ $card }) => $card};
  border: 1px solid ${({ $border }) => $border};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: ${({ $color }) => $color};
  font-family: 'Geist Mono', monospace;
`

const CardLink = styled.a<{ $color: string }>`
  font-weight: 600;
  font-size: 14px;
  color: ${({ $color }) => $color};
  display: inline-flex;
  align-items: center;
  gap: 4px;
  text-decoration: none;
  margin-top: auto;
  &:hover {
    text-decoration: underline;
  }
`

const cardVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: i * 0.12 },
  }),
}

const headerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
}

const Ecosystem: React.FC = () => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const p = palette(theme.isDark)

  const dapps = [
    {
      icon: '🌐',
      tint: p.tintTrade,
      title: t('Zaphod Domains'),
      description: t('Claim your .qom name — a permanent, on-chain identity minted as an ERC-721 NFT on QL1. No renewals, no expiry.'),
      tags: ['.qom', 'NFT', 'ERC-721'],
      linkLabel: t('Get a .qom domain'),
      href: 'https://domains.zaphod.one',
    },
    {
      icon: '⇆',
      tint: p.tintBridge,
      title: t('QOMX Bridge'),
      description: t('Move USDT and other assets between Ethereum, BSC and QL1 with a simple, non-custodial cross-chain bridge.'),
      tags: ['Ethereum', 'BSC', 'QL1'],
      linkLabel: t('Open Bridge'),
      href: 'https://bridge.qomx.io',
    },
    {
      icon: '◈',
      tint: p.tintEarn,
      title: t('QOM X Exchange'),
      description: t('Trade on QOM\'s hybrid exchange — limit and market orders, a native launchpad, and deep QL1 liquidity.'),
      tags: ['DEX', 'Limit orders', 'Launchpad'],
      linkLabel: t('Open Exchange'),
      href: 'https://qomx.io',
    },
  ]

  return (
    <Section>
      <motion.div
        variants={headerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-80px' }}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      >
        <Header>
          <Eyebrow color={p.cyan}>ECOSYSTEM</Eyebrow>
          <Title color={p.fg}>{t('Explore the QOM ecosystem')}</Title>
          <Text fontSize="17px" color={p.fgSecondary} style={{ lineHeight: 1.5 }}>
            {t('QOM Chain is growing. Discover the dapps and protocols building natively on QL1.')}
          </Text>
        </Header>
      </motion.div>

      <Grid>
        {dapps.map((dapp, i) => (
          <DappCard
            key={dapp.href}
            $bg={dapp.tint}
            $border={p.border}
            custom={i}
            variants={cardVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            whileHover={{ y: -6, boxShadow: '0 16px 48px rgba(10, 26, 51, 0.16)', transition: { duration: 0.22 } }}
          >
            <Icon $color={p.cyan}>{dapp.icon}</Icon>
            <Text fontSize="22px" bold color={p.fg} style={{ letterSpacing: '-0.4px', lineHeight: 1.15 }}>
              {dapp.title}
            </Text>
            <Text fontSize="14px" color={p.fgSecondary} style={{ lineHeight: 1.5 }}>
              {dapp.description}
            </Text>
            <TagRow>
              {dapp.tags.map((tag) => (
                <Tag key={tag} $card={p.card} $border={p.border} $color={p.fgSecondary}>
                  {tag}
                </Tag>
              ))}
            </TagRow>
            <CardLink $color={p.fg} href={dapp.href} target="_blank" rel="noopener noreferrer">
              {dapp.linkLabel} →
            </CardLink>
          </DappCard>
        ))}
      </Grid>
    </Section>
  )
}

export default Ecosystem
